import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthProvider';
import type { PickedFile } from '@/lib/fileTree';
import { supabase } from '@/lib/supabase';
import type {
  AdminLibrary,
  CompanyContractKind,
  CompanyContractStatus,
  CompanyCredentialKind,
  Inserts,
  Tables,
} from '@/types/database';
import {
  ADMIN_FILES_BUCKET,
  MAX_ADMIN_FILE_BYTES,
  adminFileOpensInline,
  adminLibraryAllowedSummary,
  adminUploadContentType,
  blankToNull,
  isAllowedAdminFile,
  safeStorageName,
} from './model';

export type AdminFolder = Tables<'admin_folders'>;
export type AdminFile = Tables<'admin_files'>;
export type CompanyProfile = Tables<'company_profiles'>;
export type CompanyCredential = Tables<'company_credentials'>;
export type CompanyContract = Tables<'company_contracts'>;
export type CompanyContact = Tables<'company_contacts'>;

export type AdminFileWithUploader = AdminFile & {
  uploader: { id: string; full_name: string | null; email: string } | null;
};

const ADMIN_FILE_SELECT =
  'id, organization_id, library, folder_id, name, description, storage_path, mime_type, size_bytes, uploaded_by, created_at, updated_at, uploader:uploaded_by(id, full_name, email)';

export const adminKeys = {
  all: ['admin'] as const,
  folders: (library: AdminLibrary) => [...adminKeys.all, 'folders', library] as const,
  files: (library: AdminLibrary) => [...adminKeys.all, 'files', library] as const,
  profile: () => [...adminKeys.all, 'profile'] as const,
  credentials: () => [...adminKeys.all, 'credentials'] as const,
  contracts: () => [...adminKeys.all, 'contracts'] as const,
  contacts: () => [...adminKeys.all, 'contacts'] as const,
};

function requireOrg(organizationId: string | undefined): string {
  if (!organizationId) throw new Error('Not signed in.');
  return organizationId;
}

async function removeStored(paths: Array<string | null | undefined>) {
  const cleaned = paths.filter((path): path is string => !!path);
  if (!cleaned.length) return;
  await supabase.storage.from(ADMIN_FILES_BUCKET).remove(cleaned);
}

function assertUploadable(library: AdminLibrary, file: File) {
  if (file.size <= 0) throw new Error(`${file.name} is empty.`);
  if (file.size > MAX_ADMIN_FILE_BYTES) throw new Error('Files must be 50 MB or smaller.');
  if (!isAllowedAdminFile(library, file.name)) {
    throw new Error(
      `${file.name} isn’t an allowed type. Upload ${adminLibraryAllowedSummary(library)}.`,
    );
  }
}

async function uploadObject(organizationId: string, keyPrefix: string, file: File) {
  // Timestamp keeps a replacement (same record, same filename) from
  // colliding with the object that's about to be deleted.
  const path = `${organizationId}/${keyPrefix}/${Date.now()}-${safeStorageName(file.name)}`;
  const { error } = await supabase.storage.from(ADMIN_FILES_BUCKET).upload(path, file, {
    upsert: false,
    cacheControl: '3600',
    contentType: adminUploadContentType(file),
  });
  if (error) throw error;
  return path;
}

/**
 * Opens a stored file in a new tab. PDFs and images preview inline; other
 * types (fonts, design sources, decks) are served as an attachment so the
 * browser saves them instead of showing a blank or garbled tab.
 */
export async function openAdminFile(storagePath: string, displayName?: string) {
  const name = displayName ?? storagePath;
  const { data, error } = await supabase.storage
    .from(ADMIN_FILES_BUCKET)
    .createSignedUrl(storagePath, 60 * 5, {
      download: adminFileOpensInline(name) ? false : displayName || true,
    });
  if (error || !data?.signedUrl) throw error ?? new Error('Could not open that file.');
  window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
}

// ---------------------------------------------------------------------------
// Drive
// ---------------------------------------------------------------------------

export function useAdminFolders(library: AdminLibrary) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: adminKeys.folders(library),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_folders')
        .select('id, organization_id, library, parent_id, name, created_by, created_at, updated_at')
        .eq('library', library)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as AdminFolder[];
    },
  });
}

export function useAdminFiles(library: AdminLibrary) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: adminKeys.files(library),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_files')
        .select(ADMIN_FILE_SELECT)
        .eq('library', library)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as AdminFileWithUploader[];
    },
  });
}

export function useCreateAdminFolder() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { library: AdminLibrary; parentId: string | null; name: string }) => {
      const organizationId = requireOrg(profile?.organization_id);
      const name = input.name.trim();
      if (!name) throw new Error('Folder name is required.');
      const payload: Inserts<'admin_folders'> = {
        organization_id: organizationId,
        library: input.library,
        parent_id: input.parentId,
        name,
        created_by: profile?.id ?? null,
      };
      const { data, error } = await supabase
        .from('admin_folders')
        .insert(payload)
        .select('id, organization_id, library, parent_id, name, created_by, created_at, updated_at')
        .single();
      if (error) throw error;
      return data as AdminFolder;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.folders(vars.library) });
    },
  });
}

export function useSeedAdminFolders() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { library: AdminLibrary; names: readonly string[] }) => {
      const organizationId = requireOrg(profile?.organization_id);
      const payload: Inserts<'admin_folders'>[] = input.names.map((name) => ({
        organization_id: organizationId,
        library: input.library,
        parent_id: null,
        name,
        created_by: profile?.id ?? null,
      }));
      const { error } = await supabase.from('admin_folders').insert(payload);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.folders(vars.library) });
    },
  });
}

export function useRenameAdminFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { library: AdminLibrary; id: string; name: string }) => {
      const name = input.name.trim();
      if (!name) throw new Error('Folder name is required.');
      const { error } = await supabase.from('admin_folders').update({ name }).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.folders(vars.library) });
    },
  });
}

export function useMoveAdminFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { library: AdminLibrary; id: string; parentId: string | null }) => {
      const { error } = await supabase
        .from('admin_folders')
        .update({ parent_id: input.parentId })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.folders(vars.library) });
    },
  });
}

export function useDeleteAdminFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { library: AdminLibrary; id: string; storagePaths: string[] }) => {
      const { error } = await supabase.from('admin_folders').delete().eq('id', input.id);
      if (error) throw error;
      await removeStored(input.storagePaths);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.folders(vars.library) });
      void qc.invalidateQueries({ queryKey: adminKeys.files(vars.library) });
    },
  });
}

export interface AdminUploadInput {
  library: AdminLibrary;
  /** Where loose files land, and the root under which picked folders are mirrored. */
  folderId: string | null;
  files: PickedFile[];
  onProgress?: (done: number, total: number) => void;
}

export interface AdminUploadResult {
  uploaded: number;
  foldersCreated: number;
  failures: string[];
}

function siblingKey(parentId: string | null, name: string) {
  return `${parentId ?? 'root'}\u0000${name.trim().toLowerCase()}`;
}

/**
 * Walks `dirs` under `rootId`, reusing folders that already exist (matched
 * the way the DB does: case-insensitive among siblings) and creating the
 * rest. `byKey` is a shared lookup so repeated paths cost nothing.
 */
async function ensureFolderPath(args: {
  organizationId: string;
  library: AdminLibrary;
  rootId: string | null;
  dirs: string[];
  byKey: Map<string, string>;
  createdBy: string | null;
  onCreated: () => void;
}): Promise<string | null> {
  let parentId = args.rootId;
  for (const rawName of args.dirs) {
    const name = rawName.trim().slice(0, 80);
    if (!name) continue;
    const key = siblingKey(parentId, name);
    let id = args.byKey.get(key);
    if (!id) {
      const payload: Inserts<'admin_folders'> = {
        organization_id: args.organizationId,
        library: args.library,
        parent_id: parentId,
        name,
        created_by: args.createdBy,
      };
      const { data, error } = await supabase
        .from('admin_folders')
        .insert(payload)
        .select('id')
        .single();
      if (error) {
        // Someone else created it between our lookup and insert; adopt theirs.
        let lookup = supabase.from('admin_folders').select('id').eq('library', args.library);
        lookup = parentId ? lookup.eq('parent_id', parentId) : lookup.is('parent_id', null);
        const { data: existing } = await lookup.ilike('name', name).maybeSingle();
        if (!existing?.id) throw error;
        id = existing.id as string;
      } else {
        id = (data as { id: string }).id;
        args.onCreated();
      }
      args.byKey.set(key, id);
    }
    parentId = id;
  }
  return parentId;
}

export function useUploadAdminFiles() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: AdminUploadInput): Promise<AdminUploadResult> => {
      const organizationId = requireOrg(profile?.organization_id);
      const failures: string[] = [];
      const total = input.files.length;
      let done = 0;
      let uploaded = 0;
      let foldersCreated = 0;
      const report = () => input.onProgress?.(done, total);

      // Reject bad files up front so a folder full of them doesn't leave
      // behind an empty tree.
      const accepted: PickedFile[] = [];
      for (const item of input.files) {
        try {
          assertUploadable(input.library, item.file);
          accepted.push(item);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Upload failed';
          failures.push(`${item.file.name}: ${message}`);
          done += 1;
        }
      }
      report();

      const needsFolders = accepted.some((item) => item.dirs.length > 0);
      const byKey = new Map<string, string>();
      if (needsFolders) {
        const { data, error } = await supabase
          .from('admin_folders')
          .select('id, parent_id, name')
          .eq('library', input.library);
        if (error) throw error;
        for (const folder of (data ?? []) as Array<
          Pick<AdminFolder, 'id' | 'parent_id' | 'name'>
        >) {
          byKey.set(siblingKey(folder.parent_id, folder.name), folder.id);
        }
      }

      const folderIdForDirs = new Map<string, string | null>();
      for (const item of accepted) {
        const dirKey = item.dirs.map((dir) => dir.trim().toLowerCase()).join('/');
        let path: string | null = null;
        try {
          let folderId = input.folderId;
          if (item.dirs.length) {
            if (!folderIdForDirs.has(dirKey)) {
              folderIdForDirs.set(
                dirKey,
                await ensureFolderPath({
                  organizationId,
                  library: input.library,
                  rootId: input.folderId,
                  dirs: item.dirs,
                  byKey,
                  createdBy: profile?.id ?? null,
                  onCreated: () => {
                    foldersCreated += 1;
                  },
                }),
              );
            }
            folderId = folderIdForDirs.get(dirKey) ?? input.folderId;
          }

          const file = item.file;
          const id = crypto.randomUUID();
          path = await uploadObject(organizationId, id, file);
          const payload: Inserts<'admin_files'> = {
            id,
            organization_id: organizationId,
            library: input.library,
            folder_id: folderId,
            name: file.name.trim(),
            storage_path: path,
            mime_type: adminUploadContentType(file),
            size_bytes: file.size,
            uploaded_by: profile?.id ?? null,
          };
          const { error } = await supabase.from('admin_files').insert(payload);
          if (error) throw error;
          uploaded += 1;
        } catch (err) {
          if (path) await removeStored([path]);
          const message = err instanceof Error ? err.message : 'Upload failed';
          failures.push(`${item.file.name}: ${message}`);
        } finally {
          done += 1;
          report();
        }
      }
      return { uploaded, foldersCreated, failures };
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.files(vars.library) });
      void qc.invalidateQueries({ queryKey: adminKeys.folders(vars.library) });
    },
  });
}

export function useUpdateAdminFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      library: AdminLibrary;
      id: string;
      name?: string;
      description?: string | null;
      folderId?: string | null;
    }) => {
      const patch: {
        name?: string;
        description?: string | null;
        folder_id?: string | null;
      } = {};
      if (input.name !== undefined) {
        const name = input.name.trim();
        if (!name) throw new Error('File name is required.');
        patch.name = name;
      }
      if (input.description !== undefined) patch.description = blankToNull(input.description);
      if (input.folderId !== undefined) patch.folder_id = input.folderId;
      const { error } = await supabase.from('admin_files').update(patch).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.files(vars.library) });
    },
  });
}

export function useDeleteAdminFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { library: AdminLibrary; id: string; storagePath: string }) => {
      const { error } = await supabase.from('admin_files').delete().eq('id', input.id);
      if (error) throw error;
      await removeStored([input.storagePath]);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: adminKeys.files(vars.library) });
    },
  });
}

// ---------------------------------------------------------------------------
// Company profile
// ---------------------------------------------------------------------------

export interface CompanyProfileInput {
  legalName: string;
  dba: string;
  ein: string;
  entityType: string;
  stateOfIncorporation: string;
  /** `YYYY-MM-DD`, or empty when not recorded. */
  formationDate: string;
  website: string;
  phone: string;
  email: string;
  mailingLine1: string;
  mailingLine2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  physicalLine1: string;
  physicalLine2: string;
  physicalCity: string;
  physicalState: string;
  physicalZip: string;
  notes: string;
}

export function useCompanyProfile() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: adminKeys.profile(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase.from('company_profiles').select('*').maybeSingle();
      if (error) throw error;
      return (data ?? null) as CompanyProfile | null;
    },
  });
}

export function useSaveCompanyProfile() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CompanyProfileInput) => {
      const organizationId = requireOrg(profile?.organization_id);
      const payload: Inserts<'company_profiles'> = {
        organization_id: organizationId,
        updated_by: profile?.id ?? null,
        legal_name: blankToNull(input.legalName),
        dba: blankToNull(input.dba),
        ein: blankToNull(input.ein),
        entity_type: blankToNull(input.entityType),
        state_of_incorporation: blankToNull(input.stateOfIncorporation),
        formation_date: blankToNull(input.formationDate),
        website: blankToNull(input.website),
        phone: blankToNull(input.phone),
        email: blankToNull(input.email),
        mailing_line1: blankToNull(input.mailingLine1),
        mailing_line2: blankToNull(input.mailingLine2),
        mailing_city: blankToNull(input.mailingCity),
        mailing_state: blankToNull(input.mailingState),
        mailing_zip: blankToNull(input.mailingZip),
        physical_line1: blankToNull(input.physicalLine1),
        physical_line2: blankToNull(input.physicalLine2),
        physical_city: blankToNull(input.physicalCity),
        physical_state: blankToNull(input.physicalState),
        physical_zip: blankToNull(input.physicalZip),
        notes: blankToNull(input.notes),
      };
      const { error } = await supabase
        .from('company_profiles')
        .upsert(payload, { onConflict: 'organization_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.profile() });
    },
  });
}

// ---------------------------------------------------------------------------
// Licenses, contracts, contacts
// ---------------------------------------------------------------------------

export interface CredentialInput {
  id?: string;
  kind: CompanyCredentialKind;
  name: string;
  issuer: string;
  identifier: string;
  issuedOn: string;
  expiresOn: string;
  notes: string;
  file: File | null;
  removeFile: boolean;
  existingStoragePath?: string | null;
}

export interface ContractInput {
  id?: string;
  title: string;
  counterparty: string;
  kind: CompanyContractKind;
  status: CompanyContractStatus;
  effectiveOn: string;
  expiresOn: string;
  autoRenews: boolean;
  notes: string;
  file: File | null;
  removeFile: boolean;
  existingStoragePath?: string | null;
}

export interface ContactInput {
  id?: string;
  name: string;
  company: string;
  role: string;
  email: string;
  phone: string;
  notes: string;
}

async function stageRecordFile(organizationId: string, recordId: string, file: File) {
  assertUploadable('templates', file);
  const path = await uploadObject(organizationId, `records/${recordId}`, file);
  return {
    storage_path: path,
    file_name: file.name.trim(),
    mime_type: adminUploadContentType(file),
    size_bytes: file.size,
  };
}

export function useCompanyCredentials() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: adminKeys.credentials(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_credentials')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CompanyCredential[];
    },
  });
}

export function useSaveCompanyCredential() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CredentialInput) => {
      const organizationId = requireOrg(profile?.organization_id);
      const name = input.name.trim();
      if (!name) throw new Error('Name is required.');
      const id = input.id ?? crypto.randomUUID();
      let uploadedPath: string | null = null;
      let filePatch: Partial<Inserts<'company_credentials'>> = {};
      if (input.file) {
        const staged = await stageRecordFile(organizationId, id, input.file);
        uploadedPath = staged.storage_path;
        filePatch = staged;
      } else if (input.removeFile) {
        filePatch = {
          storage_path: null,
          file_name: null,
          mime_type: null,
          size_bytes: null,
        };
      }

      const fields = {
        kind: input.kind,
        name,
        issuer: blankToNull(input.issuer),
        identifier: blankToNull(input.identifier),
        issued_on: blankToNull(input.issuedOn),
        expires_on: blankToNull(input.expiresOn),
        notes: blankToNull(input.notes),
        ...filePatch,
      };

      if (input.id) {
        const { error } = await supabase.from('company_credentials').update(fields).eq('id', id);
        if (error) {
          await removeStored([uploadedPath]);
          throw error;
        }
      } else {
        const payload: Inserts<'company_credentials'> = {
          id,
          organization_id: organizationId,
          created_by: profile?.id ?? null,
          ...fields,
        };
        const { error } = await supabase.from('company_credentials').insert(payload);
        if (error) {
          await removeStored([uploadedPath]);
          throw error;
        }
      }

      if (
        (input.file || input.removeFile) &&
        input.existingStoragePath &&
        input.existingStoragePath !== uploadedPath
      ) {
        await removeStored([input.existingStoragePath]);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.credentials() });
    },
  });
}

export function useDeleteCompanyCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; storagePath: string | null }) => {
      const { error } = await supabase.from('company_credentials').delete().eq('id', input.id);
      if (error) throw error;
      await removeStored([input.storagePath]);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.credentials() });
    },
  });
}

export function useCompanyContracts() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: adminKeys.contracts(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_contracts')
        .select('*')
        .order('title', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CompanyContract[];
    },
  });
}

export function useSaveCompanyContract() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: ContractInput) => {
      const organizationId = requireOrg(profile?.organization_id);
      const title = input.title.trim();
      if (!title) throw new Error('Title is required.');
      const id = input.id ?? crypto.randomUUID();
      let uploadedPath: string | null = null;
      let filePatch: Partial<Inserts<'company_contracts'>> = {};
      if (input.file) {
        const staged = await stageRecordFile(organizationId, id, input.file);
        uploadedPath = staged.storage_path;
        filePatch = staged;
      } else if (input.removeFile) {
        filePatch = {
          storage_path: null,
          file_name: null,
          mime_type: null,
          size_bytes: null,
        };
      }

      const fields = {
        title,
        counterparty: blankToNull(input.counterparty),
        kind: input.kind,
        status: input.status,
        effective_on: blankToNull(input.effectiveOn),
        expires_on: blankToNull(input.expiresOn),
        auto_renews: input.autoRenews,
        notes: blankToNull(input.notes),
        ...filePatch,
      };

      if (input.id) {
        const { error } = await supabase.from('company_contracts').update(fields).eq('id', id);
        if (error) {
          await removeStored([uploadedPath]);
          throw error;
        }
      } else {
        const payload: Inserts<'company_contracts'> = {
          id,
          organization_id: organizationId,
          created_by: profile?.id ?? null,
          ...fields,
        };
        const { error } = await supabase.from('company_contracts').insert(payload);
        if (error) {
          await removeStored([uploadedPath]);
          throw error;
        }
      }

      if (
        (input.file || input.removeFile) &&
        input.existingStoragePath &&
        input.existingStoragePath !== uploadedPath
      ) {
        await removeStored([input.existingStoragePath]);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.contracts() });
    },
  });
}

export function useDeleteCompanyContract() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; storagePath: string | null }) => {
      const { error } = await supabase.from('company_contracts').delete().eq('id', input.id);
      if (error) throw error;
      await removeStored([input.storagePath]);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.contracts() });
    },
  });
}

export function useCompanyContacts() {
  const { profile } = useAuth();
  return useQuery({
    queryKey: adminKeys.contacts(),
    enabled: !!profile,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_contacts')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CompanyContact[];
    },
  });
}

export function useSaveCompanyContact() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: ContactInput) => {
      const organizationId = requireOrg(profile?.organization_id);
      const name = input.name.trim();
      if (!name) throw new Error('Name is required.');
      const fields = {
        name,
        company: blankToNull(input.company),
        role: blankToNull(input.role),
        email: blankToNull(input.email),
        phone: blankToNull(input.phone),
        notes: blankToNull(input.notes),
      };
      if (input.id) {
        const { error } = await supabase.from('company_contacts').update(fields).eq('id', input.id);
        if (error) throw error;
      } else {
        const payload: Inserts<'company_contacts'> = {
          organization_id: organizationId,
          created_by: profile?.id ?? null,
          ...fields,
        };
        const { error } = await supabase.from('company_contacts').insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.contacts() });
    },
  });
}

export function useDeleteCompanyContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('company_contacts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.contacts() });
    },
  });
}
